import { Injectable } from '@angular/core';
import { SettingsService } from 'services/settings.service';
import { DateTime } from 'luxon';
import { DeferredPromise, LocaleSettings } from '@cf-shared/shared-util';
import { NutritionLabelStyle } from '@cf-shared/enums';
import { LoggingService } from './logging.service';
import { GeoIPInfo } from '@cf-shared/get-locale-interface';
import { CloudFunctionsService } from './cloud-functions.service';
import { isLocaleFetchFailureExpected } from './locale-fetch-failure';
import { isE2ETest } from 'environments/environment-helpers';
import { CS_UI_LANGUAGE_KEY, CS_UI_LANGUAGE_UID_KEY, CS_UI_LANGUAGE_UNCONFIRMED_KEY, firstRunLanguageDefault } from 'i18n/ui-language';

export function getLocaleSeparators(localeName: string): Partial<LocaleSettings> {
  // The trick to getting locale separators by parsing what Number.toLocaleString for a decimal number generates
  // originates from this thread: https://stackoverflow.com/a/2085299/1664811 .
  let testString = Number(1234.56).toLocaleString(localeName);
  let [, thousandsSeparator, decimalSeparator] = testString.match(/^1(.?)234(.?)56$/) || ['', null, null];

  return { thousandsSeparator, decimalSeparator };
}

export function getLocale24Clock(localeName: string): boolean {
  // this is the list of countries that use 12-hour clock
  // https://en.wikipedia.org/wiki/12-hour_clock#List_of_countries_where_the_12-hour_clock_is_dominate
  // https://stackoverflow.com/a/8888498/1664811
  let twelveHourClockCountries = [
    'AU', // Australia
    'CA', // Canada
    'EG', // Egypt
    'GB', // United Kingdom
    'IE', // Ireland
    'IN', // India
    'NZ', // New Zealand
    'PH', // Philippines
    'PK', // Pakistan
    'US', // United States
    'ZA', // South Africa
    'MX', // Mexico
  ];

  let localeNameUpper = localeName.toUpperCase();

  return !twelveHourClockCountries.some(l => l === localeNameUpper);
}

// returns 1=Monday, 6=Saturday, 7=Sunday
export function getLocaleFirstDayOfWeek(localeName: string): number {
  // try modern Intl API first (available in modern browsers)
  try {
    const locale = new Intl.Locale(localeName);
    // getWeekInfo() returns { firstDay: 1-7, weekend: [...], minimalDays: n }
    // where firstDay: 1=Monday, 6=Saturday, 7=Sunday
    if ('getWeekInfo' in locale) {
      const weekInfo = (locale as any).getWeekInfo();
      if (weekInfo?.firstDay) {
        return weekInfo.firstDay;
      }
    }
    // older API (deprecated but still works in some browsers)
    if ('weekInfo' in locale) {
      const weekInfo = (locale as any).weekInfo;
      if (weekInfo?.firstDay) {
        return weekInfo.firstDay;
      }
    }
  } catch {
    // ignore errors from Intl API
  }

  // fallback: hardcoded list based on country code
  const sundayFirstCountries = ['US', 'CA', 'JP', 'TW', 'KR', 'IL', 'PH', 'PR', 'BR', 'MX', 'AU', 'NZ', 'IN', 'ZA'];
  const saturdayFirstCountries = ['AF', 'DZ', 'BH', 'BD', 'EG', 'IR', 'IQ', 'JO', 'KW', 'LY', 'MV', 'OM', 'QA', 'SA', 'SD', 'SY', 'AE', 'YE'];

  const code = localeName.toUpperCase();
  if (saturdayFirstCountries.includes(code)) return 6;
  if (sundayFirstCountries.includes(code)) return 7;
  return 1; // Monday is default for most of the world
}

const currentLocaleSettingsVersion = 3;

@Injectable()
export class LocationService {
  locationReady = DeferredPromise.defer<void>();

  // call initializeAsync after construction, but only after settings are loaded
  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
    private cloudFunctions: CloudFunctionsService,
  ) { }

  /** Use existing cached locale if available, otherwise default to US */
  private fallbackToExistingLocale(locale: typeof this.settings.locale) {
    if (!locale.localeName) {
      // first time user with no cached locale - initialize with defaults (a
      // genuine new install → the UI-language default may apply)
      locale.localeName = 'US';
      this.initializeLocaleValues(locale, 'US', true);
      console.warn('No cached locale available, defaulting to US');
    } else if (!locale.decimalSeparator) {
      // have locale name but missing derived values (shouldn't happen, but be
      // safe) - NOT a new install, so the UI-language default must not apply
      this.initializeLocaleValues(locale, locale.localeName, false);
      console.warn(`Using cached locale "${locale.localeName}" (reinitialized values)`);
    } else {
      console.warn(`Using cached locale "${locale.localeName}"`);
    }
    // note: don't update lastCheck - we want to retry fetching next session
    this.locationReady.resolve(null);
  }

  /** Initialize locale-derived values (separators, clock format, week start).
   * `isInitialSetup` is true only on a genuine first-time locale setup (no
   * cached locale yet) — it gates the one-time UI-language default so a later
   * re-initialization (a geo country change, or a `currentLocaleSettingsVersion`
   * bump) never applies it to an existing user. */
  private initializeLocaleValues(locale: typeof this.settings.locale, localeName: string, isInitialSetup: boolean) {
    const separators = getLocaleSeparators(localeName);
    locale.decimalSeparator = separators.decimalSeparator;
    locale.thousandsSeparator = separators.thousandsSeparator;
    locale.clock24hr = getLocale24Clock(localeName);
    locale.weekStartDay = getLocaleFirstDayOfWeek(localeName);

    if (!this.settings.nutritionLabelStyle) {
      if (['US', 'CA', 'MX'].some(l => l === localeName)) {
        this.settings.nutritionLabelStyle = NutritionLabelStyle.US;
      } else {
        this.settings.nutritionLabelStyle = NutritionLabelStyle.EU;
      }
    }

    // First-run UI language default (#4147). firstRunLanguageDefault returns a
    // locale ONLY on a genuine new install (isInitialSetup) with no language
    // chosen yet — else null. Gating on isInitialSetup keeps this away from
    // EXISTING users (whose locale may re-initialize on a geo change or a
    // settings-version bump); they keep English unless they pick a language in
    // Settings, and the sign-in reconciliation leaves them alone too. When it
    // does return a locale, write BOTH keys; a new non-English user then gets one
    // reconciliation reload behind the boot splash to bind the language (see
    // design-docs/i18n-translation.md).
    const defaultLang = firstRunLanguageDefault(isInitialSetup, this.settings.uiLanguage);
    if (defaultLang) {
      this.settings.uiLanguage = defaultLang;
      localStorage.setItem(CS_UI_LANGUAGE_KEY, defaultLang);
      // Clear both markers recorded for the PREVIOUS device language. This one is
      // a guess from the browser, not a deliberate choice, so nobody owns it and
      // nothing about it is pending confirmation. Leaving either behind would let
      // the sign-in reconciliation mistake this guess for a user's own recent
      // choice and keep it (see decideUiLanguageReconcile). An unowned, unpending
      // key stays resettable, which is the safe direction.
      localStorage.removeItem(CS_UI_LANGUAGE_UID_KEY);
      localStorage.removeItem(CS_UI_LANGUAGE_UNCONFIRMED_KEY);
    }
  }

  // must manually init location during boot, so it doesn't start before the settings are loaded!
  async initializeAsync() {
    console.log('Locale: Initializing...');
    let locale = this.settings.locale;

    // query at most once per day
    let now = DateTime.utc();
    if (locale.lastCheck && now.hasSame(DateTime.fromISO(locale.lastCheck), 'day') && locale.lastVersion === currentLocaleSettingsVersion) {
      console.log(`Locale: using cached locale "${locale.localeName}"`);
      this.locationReady.resolve(null);
      return;
    }

    // fetch locale from server - this can fail with network or server errors
    let result: Awaited<ReturnType<typeof this.cloudFunctions.getLocaleAsync>>;
    try {
      result = await this.cloudFunctions.getLocaleAsync({});
    } catch (error) {
      // if it is a network failure, an abort, or a failure the cloud-functions layer already
      // reported (or skipped because the phone was offline), only log it: a second report
      // would be a duplicate (df-e9009a88832f). See isLocaleFetchFailureExpected.
      if (isLocaleFetchFailureExpected(error)) {
        console.warn('Network error fetching locale:', error.message);
      } else {
        // unexpected error that nobody reported yet - report to Sentry
        this.logging.trackSilentProblem('Unexpected error fetching locale', error);
      }
      this.fallbackToExistingLocale(locale);
      return;
    }

    // server returned an error response (e.g., ipapi.co failed)
    if (result.error) {
      console.warn('Server error fetching locale:', result.error);
      this.fallbackToExistingLocale(locale);
      return;
    }

    // success - process the locale data
    let ipInfo: Partial<GeoIPInfo> = result.geoInfo || {};
    let localeName = ipInfo['country_code'] || 'US';

    // during E2E tests, fake Norwegian, which should give us metric units for faster execution
    // (normally, E2E servers are in the US, then tests have to manually toggle units every time they delete the user)
    if (isE2ETest()) {
      localeName = 'NO';
    }

    // uncomment this to fake a locale
    //localeName = 'GB';

    // always update the IP, if present
    if (Object.keys(ipInfo).length > 0) {
      locale.lastKnownIpInfo = ipInfo as GeoIPInfo;
    }

    // when locale changed, initialize values. isInitialSetup is true only when
    // there was no cached locale before now — so a genuine new install applies
    // the UI-language default, but an existing user whose geo country changed or
    // who crossed a settings-version bump does NOT.
    if (localeName !== locale.localeName || locale.lastVersion !== currentLocaleSettingsVersion) {
      const isInitialSetup = !locale.localeName;
      locale.localeName = localeName;
      locale.lastVersion = currentLocaleSettingsVersion;
      this.initializeLocaleValues(locale, localeName, isInitialSetup);
    }

    locale.lastCheck = now.toISO();
    console.log(`Locale: determined locale: "${locale.localeName}"`);
    this.settings.saveInBackground('determined locale');

    this.locationReady.resolve(null);
  }
}
